// PATH: src/pages/StudentFormPage.tsx — formulaire d'inscription (élève + tuteurs d'un bloc)
import React, { useState } from "react";
import {
  Button, Card, Col, DatePicker, Divider, Form, Input, Radio, Result, Row, Select, Space, Spin, Switch, Typography, message,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useSchoolApi } from "../api/school";
import { WHATSAPP_ENABLED } from "../config/features";
import { useIdentityApi } from "../api/identity";
import { useUnsavedGuard, confirmDiscard } from "../hooks/useUnsavedGuard";
import { useCanEditStudents } from "../hooks/useCanEditStudents";
import type { GuardianInput } from "../models/school";

const relationOptions = [
  { value: "PERE", label: "Père" },
  { value: "MERE", label: "Mère" },
  { value: "TUTEUR", label: "Tuteur" },
  { value: "AUTRE", label: "Autre" },
];

// Remonte le message métier du backend (tc_tilmischool_be : {"error": ...},
// relayant celui de tc_identity) plutôt qu'un « Échec » générique.
const getErrorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  if (typeof data?.error === "string" && data.error.trim() !== "") return data.error;
  if (typeof data?.message === "string" && data.message.trim() !== "") return data.message;
  return fallback;
};

const StudentFormPage: React.FC = () => {
  const api = useSchoolApi();
  const identityApi = useIdentityApi();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [generatingLogin, setGeneratingLogin] = useState(false);
  const [dirty, setDirty] = useState(false);
  useUnsavedGuard(dirty);
  const { canEdit, loading: resolvingRights } = useCanEditStudents();

  // Identifiant dérivé de l'état civil, comme dans tc_tilmicode_ui : il faut
  // donc un nom et un prénom avant de pouvoir générer.
  const generateLogin = async () => {
    try {
      const values = await form.validateFields(["first_name", "last_name"]);
      setGeneratingLogin(true);
      const login = await identityApi.generateLogin(values.first_name, values.last_name);
      form.setFieldValue(["account", "login"], login);
    } catch (err: any) {
      if (err?.errorFields) return; // erreurs de validation : déjà affichées sous les champs
      message.error(getErrorMessage(err, "Génération de l'identifiant impossible"));
    } finally {
      setGeneratingLogin(false);
    }
  };

  const onFinish = async (values: any) => {
    if (saving) return; // anti double-clic : un doublon créerait un second compte central
    const guardians: GuardianInput[] = (values.guardians ?? []).map((g: any) => ({
      ...g,
      is_primary: !!g.is_primary,
    }));

    setSaving(true);
    try {
      // Identifiant manquant (champ désactivé, bouton non cliqué) : on le génère
      // avant l'envoi plutôt que d'échouer côté backend.
      let account = values.account ?? {};
      if (!account.login) {
        account = { ...account, login: await identityApi.generateLogin(values.first_name, values.last_name) };
      }

      const payload = {
        ...values,
        birth_date: values.birth_date ? dayjs(values.birth_date).format("YYYY-MM-DDT00:00:00[Z]") : undefined,
        guardians,
        account,
      };
      const student = await api.createStudent(payload);
      // Levé AVANT de naviguer : sinon la garde se déclencherait sur une saisie
      // qui vient précisément d'être enregistrée.
      setDirty(false);
      message.success("Dossier élève et compte créés");
      navigate(`/students/${student.id}`);
    } catch (err: any) {
      message.error(getErrorMessage(err, "Échec de la création"));
    } finally {
      setSaving(false);
    }
  };

  // Le bouton « Nouvelle inscription » est masqué hors direction/surveillance,
  // mais l'URL reste saisissable : sans ce garde, un enseignant remplissait le
  // formulaire entier pour un 403 à l'enregistrement.
  if (resolvingRights) return <Spin style={{ display: "block", margin: "48px auto" }} />;
  if (!canEdit) {
    return <Result status="403" title="Inscription réservée à la direction et à la surveillance générale" />;
  }

  return (
    <Card>
      <Typography.Title level={3}>Nouvelle inscription — dossier élève</Typography.Title>
      <Form form={form} layout="vertical" onFinish={onFinish}
        // Cinq sections et une vingtaine de champs : les perdre sur une
        // fermeture d'onglet ou un « Annuler » réflexe serait coûteux.
        onValuesChange={() => setDirty(true)}
        initialValues={{ guardians: [{ relation: "PERE", is_primary: true }] }}>
        <Divider orientation="left">État civil</Divider>
        <Row gutter={16}>
          <Col xs={24} md={6}>
            <Form.Item name="last_name" label="Nom" rules={[{ required: true, message: "Nom requis" }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="first_name" label="Prénom" rules={[{ required: true, message: "Prénom requis" }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="last_name_ar" label="النسب (nom en arabe)">
              <Input dir="rtl" />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="first_name_ar" label="الاسم (prénom en arabe)">
              <Input dir="rtl" />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="massar_code" label="Code Massar">
              <Input placeholder="G123456789" />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="gender" label="Sexe">
              <Radio.Group options={[{ value: "M", label: "M" }, { value: "F", label: "F" }]} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="birth_date" label="Date de naissance">
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                // Une date future donnerait un âge négatif sur les listes et les
                // certificats. Le backend la refuse aussi (validateBirthDate) :
                // l'import Excel n'a pas de formulaire pour l'arrêter.
                disabledDate={(d) => d.isAfter(dayjs(), "day")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="birth_place" label="Lieu de naissance">
              <Input />
            </Form.Item>
          </Col>
          {/* Le CIN existait partout SAUF ici : fiche, modale de modification,
              modèle d'import. Il fallait donc créer l'élève puis rouvrir sa
              fiche pour le saisir. */}
          <Col xs={24} md={6}>
            <Form.Item name="cin" label="CIN">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="address" label="Adresse">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="city" label="Ville">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="phone" label="Téléphone élève">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Données médicales de base</Divider>
        <Row gutter={16}>
          <Col xs={24} md={6}>
            <Form.Item name="blood_type" label="Groupe sanguin">
              <Select
                allowClear
                options={["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((v) => ({ value: v, label: v }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={9}>
            <Form.Item name="allergies" label="Allergies">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={9}>
            <Form.Item name="medical_notes" label="Notes médicales">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Compte élève</Divider>
        <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
          L'élève reçoit un compte Tilmicode : même identifiant, mêmes accès que les élèves inscrits
          depuis Tilmicode. Le compte est actif immédiatement — l'email est facultatif.
        </Typography.Paragraph>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item label="Identifiant" required>
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item
                  name={["account", "login"]}
                  noStyle
                  rules={[{ required: true, message: "Générez l'identifiant" }]}
                >
                  <Input disabled placeholder="Généré depuis le nom et le prénom" />
                </Form.Item>
                <Button type="primary" onClick={generateLogin} loading={generatingLogin}>
                  Générer
                </Button>
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name={["account", "email"]}
              label="Email (facultatif)"
              rules={[{ type: "email", message: "Email invalide" }]}
            >
              <Input placeholder="nom@example.com" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["account", "password"]} label="Mot de passe">
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Parents / tuteurs</Divider>
        <Form.List name="guardians">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...rest }) => (
                <Row gutter={16} key={key} align="middle">
                  <Col xs={24} md={4}>
                    <Form.Item {...rest} name={[name, "last_name"]} label="Nom" rules={[{ required: true, message: "Requis" }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item {...rest} name={[name, "first_name"]} label="Prénom" rules={[{ required: true, message: "Requis" }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={5}>
                    <Form.Item
                      {...rest}
                      name={[name, "phone"]}
                      label={WHATSAPP_ENABLED ? "Téléphone (WhatsApp)" : "Téléphone"}
                      rules={[{ required: true, message: WHATSAPP_ENABLED ? "Requis — canal de notification" : "Téléphone requis" }]}
                    >
                      <Input placeholder="+212 6 XX XX XX XX" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item {...rest} name={[name, "relation"]} label="Relation" rules={[{ required: true }]}>
                      <Select options={relationOptions} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Item {...rest} name={[name, "is_primary"]} label="Contact principal" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={3}>
                    {fields.length > 1 && (
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)}>
                        Retirer
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}
              <Form.Item>
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ relation: "TUTEUR" })}>
                  Ajouter un tuteur
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Space>
          <Button type="primary" htmlType="submit" loading={saving}>
            Créer le dossier
          </Button>
          <Button onClick={() => { confirmDiscard(dirty).then((ok) => { if (ok) navigate(-1); }); }}>
            Annuler
          </Button>
        </Space>
      </Form>
    </Card>
  );
};

export default StudentFormPage;
